const TILE_SIZE = 512

/**
 * Downscale a material pattern to a square power-of-two tile.
 *
 * Patterns are tiled with `repeat` in the 2D preview and `RepeatWrapping` in the
 * 3D viewer, so the source pixel size decides how big one tile lands on the
 * product — an untouched 3000px photo renders as a single smeared tile. Forcing
 * every upload to the same square keeps tiling predictable regardless of what
 * the user picked, and a power of two keeps mipmaps and repeat wrapping happy on
 * every WebGL target.
 *
 * The image is drawn to fill the square (aspect ratio is deliberately not
 * preserved) because a letterboxed tile would repeat its empty bars across the
 * engraved area.
 */
const resizeToTile = (file: File, size: number = TILE_SIZE): Promise<File> =>
	new Promise((resolve, reject) => {
		const objectUrl = URL.createObjectURL(file)
		const img = new Image()

		img.onload = () => {
			URL.revokeObjectURL(objectUrl)
			try {
				const canvas = document.createElement('canvas')
				canvas.width = size
				canvas.height = size
				const ctx = canvas.getContext('2d')
				if (!ctx) {
					resolve(file)
					return
				}

				ctx.imageSmoothingEnabled = true
				ctx.imageSmoothingQuality = 'high'
				ctx.drawImage(img, 0, 0, size, size)

				canvas.toBlob((blob) => {
					if (!blob) {
						resolve(file)
						return
					}
					resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.png', {
						type: 'image/png',
					}))
				}, 'image/png')
			} catch {
				// A tile we could not resize is still better than no tile at all.
				resolve(file)
			}
		}

		img.onerror = () => {
			URL.revokeObjectURL(objectUrl)
			reject(new Error(`Could not read material pattern "${file.name}"`))
		}

		img.src = objectUrl
	})

export default resizeToTile
export { TILE_SIZE }
